import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon, CheckCircleIcon } from "@chakra-ui/icons";
import {
  FaAws,
  FaBrain,
  FaCloud,
  FaDatabase,
  FaDocker,
  FaJava,
  FaNodeJs,
  FaPython,
  FaReact,
  FaRobot,
  FaShieldAlt,
} from "react-icons/fa";
import {
  SiAnthropic,
  SiApacheairflow,
  SiApachekafka,
  SiAuth0,
  SiAwslambda,
  SiCloudflareworkers,
  SiDbt,
  SiGooglecloud,
  SiGooglegemini,
  SiGraphql,
  SiGrafana,
  SiKubernetes,
  SiLangchain,
  SiMongodb,
  SiNextdotjs,
  SiOpenai,
  SiPostgresql,
  SiRedis,
  SiServerless,
  SiSpringboot,
  SiSupabase,
  SiTerraform,
  SiTypescript,
  SiVercel,
} from "react-icons/si";
import { VscAzure } from "react-icons/vsc";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/utilities/navbar";
import Footer from "./utilities/footer";
import { theme } from "../components/utilities/theme";

const MotionBox = motion.create(Box);

const stackLayers = [
  {
    title: "Intelligence Layer",
    text: "Model integration, agent workflows, tool calling, retrieval, evaluations, multimodal features, and human handoff.",
    color: "#00FFD0",
    techs: [
      { name: "OpenAI", icon: SiOpenai, color: "#00FFD0" },
      { name: "Azure AI", icon: VscAzure, color: "#0078D4" },
      { name: "Gemini", icon: SiGooglegemini, color: "#4285F4" },
      { name: "Anthropic", icon: SiAnthropic, color: "#D97757" },
      { name: "LangChain", icon: SiLangchain, color: "#1C3C3C" },
      { name: "Python", icon: FaPython, color: "#3776AB" },
      { name: "Agents", icon: FaRobot, color: "#E83E8C" },
      { name: "RAG", icon: FaBrain, color: "#FFB400" },
    ],
  },
  {
    title: "Product & API Layer",
    text: "Interfaces, APIs, role-based workflows, portals, dashboards, backend services, and secure product modules.",
    color: "#FF6F20",
    techs: [
      { name: "React", icon: FaReact, color: "#61DAFB" },
      { name: "Next.js", icon: SiNextdotjs, color: "#F4F4F6" },
      { name: "TypeScript", icon: SiTypescript, color: "#3178C6" },
      { name: "NodeJS", icon: FaNodeJs, color: "#8CC84B" },
      { name: "Java", icon: FaJava, color: "#007396" },
      { name: "Spring Boot", icon: SiSpringboot, color: "#6DB33F" },
      { name: "GraphQL", icon: SiGraphql, color: "#E10098" },
      { name: "Auth0", icon: SiAuth0, color: "#EB5424" },
    ],
  },
  {
    title: "Data & Integration Layer",
    text: "Databases, knowledge sources, scraping pipelines, CRM sync, event streams, analytics, and reporting flows.",
    color: "#9B59B6",
    techs: [
      { name: "PostgreSQL", icon: SiPostgresql, color: "#4169E1" },
      { name: "MongoDB", icon: SiMongodb, color: "#47A248" },
      { name: "Redis", icon: SiRedis, color: "#DC382D" },
      { name: "Kafka", icon: SiApachekafka, color: "#F4F4F6" },
      { name: "Airflow", icon: SiApacheairflow, color: "#017CEE" },
      { name: "dbt", icon: SiDbt, color: "#FF694B" },
      { name: "Supabase", icon: SiSupabase, color: "#3ECF8E" },
      { name: "Cloud Data", icon: FaCloud, color: "#5BC0DE" },
    ],
  },
  {
    title: "Cloud & Operations Layer",
    text: "Multi-cloud deployments, managed containers, serverless workloads, monitoring, secrets, release workflows, and reliability planning.",
    color: "#5BC0DE",
    techs: [
      { name: "AWS", icon: FaAws, color: "#FF9900" },
      { name: "Azure", icon: VscAzure, color: "#0078D4" },
      { name: "Google Cloud", icon: SiGooglecloud, color: "#4285F4" },
      { name: "Docker", icon: FaDocker, color: "#2496ED" },
      { name: "Kubernetes", icon: SiKubernetes, color: "#326CE5" },
      { name: "Serverless", icon: SiServerless, color: "#FD5750" },
      { name: "Terraform", icon: SiTerraform, color: "#844FBA" },
      { name: "Edge Workers", icon: SiCloudflareworkers, color: "#F38020" },
    ],
  },
];

const operatingPrinciples = [
  "Pick models and tools around the workflow, not the trend.",
  "Keep business data structured enough for automation to use reliably.",
  "Design APIs and permissions before exposing automation to users.",
  "Measure assistant quality with real examples and review loops.",
  "Prefer managed cloud services when they reduce operational drag.",
];

const cloudCapabilities = [
  {
    title: "Microsoft Azure",
    icon: VscAzure,
    color: "#0078D4",
    text: "Azure App Service, Functions, Container Apps, AKS, Cosmos DB, storage, and Azure OpenAI-ready architectures.",
  },
  {
    title: "AWS Cloud",
    icon: FaAws,
    color: "#FF9900",
    text: "Lambda, ECS/Fargate, EKS, S3, RDS, API Gateway, IAM, CloudWatch, and Bedrock-ready AI workflows.",
  },
  {
    title: "Google Cloud",
    icon: SiGooglecloud,
    color: "#4285F4",
    text: "Cloud Run, GKE, BigQuery, Vertex AI, Pub/Sub, Cloud Storage, and analytics-friendly data systems.",
  },
  {
    title: "Serverless & Edge",
    icon: SiAwslambda,
    color: "#FF9900",
    text: "Event-driven functions, scheduled jobs, API gateways, Cloudflare Workers, queues, and webhook automation.",
  },
  {
    title: "Infrastructure as Code",
    icon: SiTerraform,
    color: "#844FBA",
    text: "Terraform-friendly environments, Docker images, Kubernetes manifests, secrets, and repeatable releases.",
  },
  {
    title: "Observability",
    icon: SiGrafana,
    color: "#F46800",
    text: "Logs, metrics, traces, alerting, dashboards, uptime checks, and production feedback loops.",
  },
];

const buildToolkits = [
  {
    title: "AI & Agent Tooling",
    icon: FaBrain,
    color: "#00FFD0",
    text: "OpenAI, Azure AI, Gemini, Anthropic, LangChain-style orchestration, RAG, tool calling, and evaluation loops.",
  },
  {
    title: "Frontend Delivery",
    icon: FaReact,
    color: "#61DAFB",
    text: "React, Next.js, TypeScript, component systems, forms, dashboards, portals, and Vercel-ready deployment paths.",
  },
  {
    title: "Backend Foundations",
    icon: SiSpringboot,
    color: "#6DB33F",
    text: "Node.js, Java, Spring Boot, REST, GraphQL, authentication, payments, admin modules, and integration APIs.",
  },
  {
    title: "Data Automation",
    icon: FaDatabase,
    color: "#9B59B6",
    text: "PostgreSQL, MongoDB, Redis, Kafka, Airflow, dbt, scraper pipelines, CRM sync, and analytics-ready storage.",
  },
  {
    title: "Security & Identity",
    icon: FaShieldAlt,
    color: "#D9534F",
    text: "Auth0, JWT, OAuth, role-based access, secret management, audit trails, validation, and privacy-aware workflows.",
  },
  {
    title: "Release & Scale",
    icon: SiVercel,
    color: "#F4F4F6",
    text: "Docker, Kubernetes, Terraform, serverless functions, CI/CD, preview environments, monitoring, and rollback planning.",
  },
];

export default function Technologies() {
  const {
    pageBg,
    cardBg,
    textColor,
    textSecondary,
    accentColor,
    accentCyan,
  } = theme.colors;

  return (
    <Box bg={pageBg} color={textColor} minH="100vh">
      <Navbar />

      <MotionBox
        id="main-content"
        as="main"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 18 }}>
          <Stack direction={{ base: "column", lg: "row" }} spacing={10} align="center">
            <Box flex="1.1">
              <Badge
                bg="rgba(0, 255, 208, 0.1)"
                color={accentCyan}
                border="1px solid rgba(0, 255, 208, 0.28)"
                borderRadius="full"
                px={4}
                py={1}
                mb={5}
              >
                Technologies
              </Badge>
              <Heading as="h1" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.08" mb={5}>
                A practical technology stack for products that need to ship.
              </Heading>
              <Text color={textSecondary} fontSize={{ base: "md", md: "xl" }} lineHeight="1.8">
                We combine product engineering, data systems, cloud infrastructure,
                model workflows, and security patterns so software can survive real
                users and real operations.
              </Text>
              <HStack spacing={3} flexWrap="wrap" mt={7}>
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={accentColor}
                  color="white"
                  rightIcon={<ArrowForwardIcon />}
                  borderRadius="lg"
                  px={7}
                  _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}
                >
                  Discuss Your Stack
                </Button>
                <Button
                  as={RouterLink}
                  to="/services"
                  variant="outline"
                  borderColor="rgba(0, 255, 208, 0.5)"
                  color={accentCyan}
                  borderRadius="lg"
                  px={7}
                  _hover={{ bg: "rgba(0, 255, 208, 0.08)" }}
                >
                  Match Services
                </Button>
              </HStack>
            </Box>

            <Box
              flex="0.9"
              bg="rgba(15, 23, 42, 0.78)"
              border="1px solid rgba(255, 255, 255, 0.12)"
              borderRadius="xl"
              p={{ base: 6, md: 7 }}
            >
              <Icon as={SiOpenai} color={accentCyan} boxSize={10} mb={5} />
              <Heading size="md" mb={4}>
                Technology choices follow the outcome
              </Heading>
              <VStack align="stretch" spacing={3}>
                {operatingPrinciples.map((item) => (
                  <HStack key={item} align="start" spacing={3}>
                    <CheckCircleIcon color={accentColor} mt="3px" />
                    <Text color={textSecondary}>{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Stack>
        </Container>

        <Box bg="rgba(15, 23, 42, 0.72)" py={{ base: 12, md: 16 }}>
          <Container maxW="1360px" px={{ base: 4, md: 8 }}>
            <SectionHeading
              eyebrow="Stack Layers"
              title="The systems behind useful products"
              text="Modern software works best when the intelligence layer, product layer, data layer, and operations layer are designed together."
            />
            <Stack spacing={6}>
              {stackLayers.map((layer) => (
                <Box
                  key={layer.title}
                  bg={cardBg}
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  borderRadius="xl"
                  p={{ base: 6, md: 7 }}
                >
                  <Flex
                    align={{ base: "start", lg: "center" }}
                    justify="space-between"
                    gap={6}
                    direction={{ base: "column", lg: "row" }}
                  >
                    <Box maxW="520px">
                      <Badge bg={`${layer.color}22`} color={layer.color} borderRadius="full" mb={4}>
                        {layer.title}
                      </Badge>
                      <Text color={textSecondary} lineHeight="1.8">
                        {layer.text}
                      </Text>
                    </Box>
                    <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={4} w={{ base: "100%", lg: "auto" }}>
                      {layer.techs.map((tech) => (
                        <Box
                          key={tech.name}
                          textAlign="center"
                          minW={{ base: "auto", lg: "112px" }}
                          p={4}
                          bg="rgba(255, 255, 255, 0.045)"
                          border="1px solid rgba(255, 255, 255, 0.1)"
                          borderRadius="lg"
                        >
                          <Icon as={tech.icon} color={tech.color} boxSize={8} mb={3} />
                          <Text fontSize="sm" fontWeight="bold">
                            {tech.name}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Flex>
                </Box>
              ))}
            </Stack>
          </Container>
        </Box>

        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 16 }}>
          <SectionHeading
            eyebrow="Build Tooling"
            title="Current tools across the product lifecycle"
            text="Beyond cloud hosting, we use modern AI, frontend, backend, data, security, and release tooling where it makes the product faster to build and easier to maintain."
          />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3, "2xl": 4 }} spacing={5} mb={{ base: 12, md: 16 }}>
            {buildToolkits.map((item) => (
              <Box
                key={item.title}
                bg={cardBg}
                border="1px solid rgba(255, 255, 255, 0.12)"
                borderRadius="xl"
                p={6}
              >
                <Flex
                  align="center"
                  justify="center"
                  w={12}
                  h={12}
                  borderRadius="lg"
                  bg={`${item.color}1A`}
                  border="1px solid"
                  borderColor={`${item.color}55`}
                  mb={5}
                >
                  <Icon as={item.icon} color={item.color} boxSize={6} />
                </Flex>
                <Heading size="sm" mb={3}>
                  {item.title}
                </Heading>
                <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                  {item.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <SectionHeading
            eyebrow="Cloud Platforms"
            title="Cloud choices for modern production systems"
            text="We can shape the stack around Azure, AWS, Google Cloud, serverless, containers, edge workloads, infrastructure as code, and observability needs."
          />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3, "2xl": 4 }} spacing={5} mb={{ base: 12, md: 16 }}>
            {cloudCapabilities.map((item) => (
              <Box
                key={item.title}
                bg={cardBg}
                border="1px solid rgba(255, 255, 255, 0.12)"
                borderRadius="xl"
                p={6}
              >
                <Flex
                  align="center"
                  justify="center"
                  w={12}
                  h={12}
                  borderRadius="lg"
                  bg={`${item.color}1A`}
                  border="1px solid"
                  borderColor={`${item.color}55`}
                  mb={5}
                >
                  <Icon as={item.icon} color={item.color} boxSize={6} />
                </Flex>
                <Heading size="sm" mb={3}>
                  {item.title}
                </Heading>
                <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                  {item.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <SectionHeading
            eyebrow="Implementation Readiness"
            title="What we check before building"
            text="The best implementation is often decided before a single line of production code is written."
          />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
            {[
              {
                title: "Data Shape",
                text: "Where data lives across PostgreSQL, MongoDB, object storage, vector stores, CRM tools, or analytics systems.",
              },
              {
                title: "Workflow Boundaries",
                text: "What agents, APIs, automations, and scheduled jobs can do, when they ask for approval, and when they escalate.",
              },
              {
                title: "Security Rules",
                text: "Auth0/OAuth/JWT, permissions, secrets, audit trails, data exposure, and user-role boundaries.",
              },
              {
                title: "Quality Signals",
                text: "Examples, tests, AI evaluations, logs, dashboards, monitoring, and feedback loops for improvement.",
              },
            ].map((item) => (
              <Box
                key={item.title}
                bg={cardBg}
                border="1px solid rgba(255, 255, 255, 0.12)"
                borderRadius="xl"
                p={6}
              >
                <Heading size="sm" mb={3}>
                  {item.title}
                </Heading>
                <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                  {item.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>

        <Box bg="rgba(16, 25, 38, 0.95)" py={{ base: 12, md: 16 }}>
          <Container maxW="850px" px={{ base: 4, md: 8 }} textAlign="center">
            <Icon as={FaRobot} color={accentCyan} boxSize={10} mb={4} />
            <Heading size="xl" mb={4}>
              Need help choosing the right architecture?
            </Heading>
            <Text color={textSecondary} fontSize="lg" lineHeight="1.8" mb={7}>
              We can audit your current tools, map the workflow, and recommend a
              stack that fits your product, budget, and risk level.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={accentColor}
              color="white"
              rightIcon={<ArrowForwardIcon />}
              borderRadius="lg"
              px={7}
              _hover={{ bg: "#ff853d" }}
            >
              Request Stack Review
            </Button>
          </Container>
        </Box>
      </MotionBox>

      <Footer />
    </Box>
  );
}

function SectionHeading({ eyebrow, title, text }) {
  const { accentCyan, textSecondary } = theme.colors;

  return (
    <Box mb={8}>
      <Text color={accentCyan} fontWeight="bold" mb={2}>
        {eyebrow}
      </Text>
      <Heading size={{ base: "lg", md: "xl" }} mb={3}>
        {title}
      </Heading>
      <Text color={textSecondary} maxW="760px" lineHeight="1.8">
        {text}
      </Text>
    </Box>
  );
}
